import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import {
  LocalCafeOutlined as PreferenceIcon,
  DescriptionOutlined as ClaimIcon,
  ArticleOutlined as ClaimWithSourceTextIcon,
  ViewList as AllIcon,
  HelpOutline as UnknownIcon,
} from "@mui/icons-material";
import { useTopicStats, WeekAgo } from "../hooks/useTopicStats";
import {
  GENRES,
  GENRE_LABELS,
  Genre,
  DIFFICULTIES,
  DIFFICULTY_LABELS,
  TopicStatsCategory,
  TOPIC_STATS_CATEGORY_LABELS,
} from "../types";

type FilterType = "all" | TopicStatsCategory;

const WEEK_COLORS: Record<1 | 2 | 3 | 4, { background: string; label: string }> = {
  1: { background: "#e3f2fd", label: "今週" },
  2: { background: "#e8f5e9", label: "先週" },
  3: { background: "#fff8e1", label: "2週前" },
  4: { background: "#fce4ec", label: "3週前" },
};

const TopicStatsTable: React.FC = () => {
  const { data: stats, isLoading, error } = useTopicStats();
  const [filterType, setFilterType] = useState<FilterType>("all");

  const filteredStats = useMemo(() => {
    if (!stats) return null;
    if (filterType === "all") {
      return stats.stats;
    }
    return stats.byCategory[filterType] || {};
  }, [stats, filterType]);

  const categoryTotals = useMemo(() => {
    if (!stats) {
      return { preference: 0, claim: 0, claimWithSourceText: 0, unknown: 0 };
    }

    const countCategory = (categoryStats: {
      [genre: string]: { [difficulty: number]: { count: number } };
    }) => {
      return Object.values(categoryStats).reduce(
        (sum, genres) => sum + Object.values(genres).reduce((s, c) => s + c.count, 0),
        0
      );
    };

    return {
      preference: countCategory(stats.byCategory.preference),
      claim: countCategory(stats.byCategory.claim),
      claimWithSourceText: countCategory(stats.byCategory.claimWithSourceText),
      unknown: countCategory(stats.byCategory.unknown),
    };
  }, [stats]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">統計情報の取得に失敗しました</Alert>;
  }

  if (!stats || !filteredStats) return null;

  const meta = stats.meta;
  const allGenres = Object.keys(filteredStats).sort();

  const getCountForCell = (genre: string, difficulty: number): number => {
    return filteredStats[genre]?.[difficulty]?.count || 0;
  };

  const getWeekAgoForCell = (genre: string, difficulty: number): WeekAgo => {
    return filteredStats[genre]?.[difficulty]?.latestWeekAgo || null;
  };

  const getCellStyle = (weekAgo: WeekAgo): React.CSSProperties => {
    if (weekAgo === null) return {};
    return { backgroundColor: WEEK_COLORS[weekAgo].background };
  };

  const getTotalForGenre = (genre: string) => {
    return DIFFICULTIES.reduce((sum, diff) => sum + getCountForCell(genre, diff), 0);
  };

  const getTotalForDifficulty = (difficulty: number) => {
    return allGenres.reduce((sum, genre) => sum + getCountForCell(genre, difficulty), 0);
  };

  const grandTotal = allGenres.reduce((sum, genre) => sum + getTotalForGenre(genre), 0);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          登録状況 (合計: {grandTotal}件)
          {meta && (
            <Typography variant="caption" display="block" color="textSecondary">
              総ドキュメント数: {meta.totalDocuments}件 / 有効データ: {meta.validDocuments}件
            </Typography>
          )}
        </Typography>
      </Box>

      {/* 凡例 */}
      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          色:
        </Typography>
        {([1, 2, 3, 4] as const).map((weekAgo) => (
          <Chip
            key={weekAgo}
            label={WEEK_COLORS[weekAgo].label}
            size="small"
            sx={{ backgroundColor: WEEK_COLORS[weekAgo].background }}
          />
        ))}
        <Chip label="それ以前 / 未登録" size="small" variant="outlined" />
      </Box>

      {/* 問題タイプフィルター */}
      <Box mb={2}>
        <ToggleButtonGroup
          value={filterType}
          exclusive
          onChange={(_, value) => {
            if (value !== null) setFilterType(value);
          }}
          size="small"
        >
          <ToggleButton value="all">
            <AllIcon fontSize="small" sx={{ mr: 0.5 }} />
            全て
          </ToggleButton>
          <ToggleButton value="preference">
            <PreferenceIcon fontSize="small" sx={{ mr: 0.5 }} />
            思いを伝える ({categoryTotals.preference})
          </ToggleButton>
          <ToggleButton value="claim">
            <ClaimIcon fontSize="small" sx={{ mr: 0.5 }} />
            主張を伝える ({categoryTotals.claim})
          </ToggleButton>
          <ToggleButton value="claimWithSourceText">
            <ClaimWithSourceTextIcon fontSize="small" sx={{ mr: 0.5 }} />
            課題文あり ({categoryTotals.claimWithSourceText})
          </ToggleButton>
          {categoryTotals.unknown > 0 && (
            <ToggleButton value="unknown">
              <UnknownIcon fontSize="small" sx={{ mr: 0.5 }} />
              未設定 ({categoryTotals.unknown})
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Box>

      {allGenres.length === 0 ? (
        <Alert severity="info">
          {filterType === "all"
            ? "登録された問題がありません"
            : `${TOPIC_STATS_CATEGORY_LABELS[filterType]}の問題がありません`}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>ジャンル</strong>
                </TableCell>
                {DIFFICULTIES.map((diff) => (
                  <TableCell key={diff} align="center">
                    <Typography variant="body2">難易度{diff}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {DIFFICULTY_LABELS[diff]}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <strong>計</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allGenres.map((genre) => {
                const genreTotal = getTotalForGenre(genre);
                return (
                  <TableRow key={genre}>
                    <TableCell component="th" scope="row">
                      <Typography variant="body2">
                        {GENRE_LABELS[genre as Genre] || genre}
                        {!(GENRES as readonly string[]).includes(genre) && (
                          <Chip
                            label="旧データ"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                    </TableCell>
                    {DIFFICULTIES.map((diff) => {
                      const count = getCountForCell(genre, diff);
                      const weekAgo = getWeekAgoForCell(genre, diff);
                      return (
                        <TableCell key={diff} align="center" style={getCellStyle(weekAgo)}>
                          {count > 0 ? (
                            <Tooltip
                              title={
                                weekAgo !== null
                                  ? `最新: ${WEEK_COLORS[weekAgo].label}に出題`
                                  : "直近4週では未出題"
                              }
                              arrow
                            >
                              <Chip
                                label={count}
                                size="small"
                                color={count > 10 ? "success" : count > 5 ? "warning" : "default"}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              0
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      <Chip label={genreTotal} size="small" color="primary" variant="outlined" />
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* 合計行 */}
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell component="th" scope="row">
                  <Typography variant="body2">
                    <strong>計</strong>
                  </Typography>
                </TableCell>
                {DIFFICULTIES.map((diff) => {
                  const diffTotal = getTotalForDifficulty(diff);
                  return (
                    <TableCell key={diff} align="center">
                      <Chip label={diffTotal} size="small" color="primary" variant="outlined" />
                    </TableCell>
                  );
                })}
                <TableCell align="center">
                  <Chip label={grandTotal} size="small" color="primary" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TopicStatsTable;
